import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Paper,
  Grid,
  SelectChangeEvent,
  IconButton,
  Collapse,
} from '@mui/material';
import Iconify from '../iconify';
import { ContestStatus } from 'src/enums/contest.enum';
import { ContestUIType } from 'src/types/custom.types';

interface FilterState {
  tenant: string;
  competition: string;
  status: string;
  search: string;
}

interface TableFiltersProps {
  contestsData: ContestUIType[];
  onFilterChange: (filteredData: ContestUIType[]) => void;
}

export const TableFilters = ({
  contestsData,
  onFilterChange,
}: TableFiltersProps) => {
  const [expandFilters, setExpandFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    tenant: '',
    competition: '',
    status: '',
    search: '',
  });

  // Extract unique values for dropdowns
  const uniqueValues = useMemo(() => {
    const tenants = [...new Set(contestsData.map((contest) => contest.tenant))];
    const competitions = [
      ...new Set(contestsData.map((contest) => contest.competition)),
    ];
    const statuses = ['Draft', 'Active', 'Upcoming', 'Complete'];

    return {
      tenants: tenants.filter(Boolean).sort(),
      competitions: competitions.filter(Boolean).sort(),
      statuses,
    };
  }, [contestsData]);

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    return contestsData.filter((contest) => {
      const matchesTenant =
        !filters.tenant || contest.tenant === filters.tenant;
      const matchesCompetition =
        !filters.competition || contest.competition === filters.competition;
      const matchesStatus =
        !filters.status ||
        (filters.status === 'Active' &&
          contest.status === ContestStatus.Active) ||
        (filters.status === 'Draft' &&
          contest.status === ContestStatus.Draft) ||
        (filters.status === 'Complete' &&
          contest.status === ContestStatus.Complete) ||
        (filters.status === 'Upcoming' &&
          contest.status === ContestStatus.Upcoming);
      const matchesSearch =
        !filters.search ||
        contest.name_en.toLowerCase().includes(filters.search.toLowerCase()) ||
        contest.competition
          .toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        contest.tenant?.toLowerCase().includes(filters.search.toLowerCase());

      return (
        matchesTenant && matchesCompetition && matchesStatus && matchesSearch
      );
    });
  }, [contestsData, filters]);

  // Update parent component when filters change
  React.useEffect(() => {
    onFilterChange(filteredData);
  }, [filteredData, onFilterChange]);

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange =
    (field: keyof FilterState) => (event: SelectChangeEvent<string>) => {
      handleFilterChange(field, event.target.value);
    };

  const clearFilters = () => {
    setFilters({
      tenant: '',
      competition: '',
      status: '',
      search: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== '');
  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== ''
  ).length;

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => setExpandFilters(!expandFilters)}>
          <Iconify
            icon='eva:arrow-down-outline'
            sx={{
              transform: expandFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out',
            }}
          />
        </IconButton>
        <Box sx={{ typography: 'h6', flexGrow: 1 }}>
          Filters
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size='small'
              color='primary'
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        {hasActiveFilters && (
          <Button
            startIcon={<Iconify icon='eva:close-outline' />}
            onClick={clearFilters}
            size='small'
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Collapse in={expandFilters} timeout={300} unmountOnExit>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size='small'
              label='Search'
              placeholder='Search by name, competition, tenant...'
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={filters.tenant}
                label='Tenant'
                onChange={handleSelectChange('tenant')}
              >
                <MenuItem value=''>All Tenants</MenuItem>
                {uniqueValues.tenants.map((tenant) => (
                  <MenuItem key={tenant} value={tenant}>
                    {tenant}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>Competition</InputLabel>
              <Select
                value={filters.competition}
                label='Competition'
                onChange={handleSelectChange('competition')}
              >
                <MenuItem value=''>All Competitions</MenuItem>
                {uniqueValues.competitions.map((competition) => (
                  <MenuItem key={competition} value={competition}>
                    {competition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size='small'>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label='Status'
                onChange={handleSelectChange('status')}
              >
                <MenuItem value=''>All Statuses</MenuItem>
                {uniqueValues.statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.search && (
              <Chip
                label={`Search: ${filters.search}`}
                onDelete={() => handleFilterChange('search', '')}
                size='small'
                variant='outlined'
              />
            )}
            {filters.tenant && (
              <Chip
                label={`Tenant: ${filters.tenant}`}
                onDelete={() => handleFilterChange('tenant', '')}
                size='small'
                variant='outlined'
              />
            )}
            {filters.competition && (
              <Chip
                label={`Competition: ${filters.competition}`}
                onDelete={() => handleFilterChange('competition', '')}
                size='small'
                variant='outlined'
              />
            )}
            {filters.status && (
              <Chip
                label={`Status: ${filters.status}`}
                onDelete={() => handleFilterChange('status', '')}
                size='small'
                variant='outlined'
              />
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};
